import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { getOrganizationGradeIdsHierarchyQuery } from "../modules/GradesQueries";
import { buildGradeHierarchyFromEdges } from "../modules/GradesUtils";
import _ from "lodash";

/**
 * Phase 1 hook: Fetches all grade IDs grouped by school in a single lightweight query,
 * then builds a client-side school -> [gradeIds] hierarchy.
 *
 * Fetch strategy: cache-and-network — returns cached data instantly on re-open,
 * background re-validation runs silently. Cannot be hoisted to calendar level because
 * the course and grade hierarchy queries are separate queries.
 *
 * @param {string} organizationId
 * @param {Object} gradeFilters - Optional GradeFilterInput (e.g. { curriculumProgramIds: [...] })
 * @returns {{ hierarchy: Object, allSchoolIds: string[], allGradeIds: string[], loading: boolean, error: Object }}
 */
const useGradeHierarchy = (
  organizationId,
  gradeFilters,
  skip,
  academicYearId
) => {
  const { data, loading, error } = useQuery(
    getOrganizationGradeIdsHierarchyQuery,
    {
      variables: {
        organizationId,
        gradeFilters: {
          ...gradeFilters,
          ...(academicYearId ? { academicYearIds: [academicYearId] } : {}),
        },
      },
      fetchPolicy: "cache-and-network",
      skip: !organizationId || skip,
      context: {
        useHttpLink: true,
      },
    }
  );

  const hierarchyData = useMemo(() => {
    const grades = _.get(data, "node.grades", []);

    if (_.isEmpty(grades)) {
      return {
        hierarchy: {},
        allSchoolIds: [],
        allGradeIds: [],
      };
    }

    return buildGradeHierarchyFromEdges(grades);
  }, [data]);

  return {
    hierarchy: hierarchyData.hierarchy,
    allSchoolIds: hierarchyData.allSchoolIds,
    allGradeIds: hierarchyData.allGradeIds,
    loading: loading && _.isEmpty(data),
    error,
  };
};

export default useGradeHierarchy;
